import { useState, useEffect } from "react";
import {
  X,
  Check,
  Share2,
  SlidersHorizontal,
  GitFork,
  FileText,
  Building2,
  Loader2,
} from "lucide-react";
import { acceptShare, declineShare } from "../../../../../api/sharing";
import { getFeedViewStats } from "../../../../../api/feed";
import { getFilteredGraphViewStats } from "../../../../../api/graph";

const CONTENT_TYPE_CONFIG = {
  custom_feed_view: { label: "Feed View", icon: SlidersHorizontal },
  custom_graph_view: { label: "Graph View", icon: GitFork },
  report: { label: "Report", icon: FileText },
  wiki_case: { label: "Wiki Case", icon: Building2 },
};

/* ── Stat labels ── */
const WIKI_STAT_LABELS = {
  employees: "Employees",
  court_cases: "Court Cases",
  domain_certs: "Domain Certs",
  gov_awards: "Gov Awards",
  sanctions: "Risks & Sanctions",
  press_releases: "Press Releases",
};

/* ── Stat card ── */
const StatBox = ({ label, value, color = "velBlue" }) => (
  <div className="rounded-lg border border-surfaceBorder/50 bg-surface/40 px-3 py-2.5">
    <p className="text-[18px] font-light text-mainText">
      {typeof value === "number" ? value.toLocaleString() : value}
    </p>
    <p className="text-[10px] font-light text-mutedText/60 mt-0.5">{label}</p>
  </div>
);

/* ── Tag pill ── */
const TagPill = ({ label, color }) => (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-light border border-velBlue border-x-skeletonBase"
    style={{
      backgroundColor: color + "15",
      borderColor: color + "30",
      color: color,
    }}
  >
    {label}
  </span>
);

/* ── Section header ── */
const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-light text-mutedText/50 uppercase tracking-wider mb-2">
    {children}
  </p>
);

/**
 * ShareInviteDetailPanel — right sidebar panel for share_invite messages.
 */
const ShareInviteDetailPanel = ({ msg, userUuid, onClose }) => {
  const meta = msg.share_meta || {};
  const preview = meta.preview || {};
  const isOwner = userUuid === meta.owner_uuid;
  const contentType = meta.content_type || "";
  const config =
    CONTENT_TYPE_CONFIG[contentType] || CONTENT_TYPE_CONFIG.custom_feed_view;
  const IconComponent = config.icon;
  const itemName = preview.item_name || preview.target || "Shared item";
  const iconColor = preview.item_color || "rgba(14,165,233,0.85)";

  const [status, setStatus] = useState(() => {
    if (msg.accepted_by?.includes(userUuid)) return "accepted";
    if (msg.declined_by?.includes(userUuid)) return "declined";
    return "pending";
  });
  const [loading, setLoading] = useState(false);

  /* ── Live stats for feed / graph views ── */
  const [liveStats, setLiveStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const fs = preview.filter_summary;
    if (!fs) return;

    if (contentType === "custom_feed_view") {
      const hasFilters =
        fs.target_types?.length > 0 ||
        fs.domains?.length > 0 ||
        fs.keywords?.length > 0;
      if (!hasFilters) return;
      setStatsLoading(true);
      getFeedViewStats({
        target_types: fs.target_types || [],
        domains: fs.domains || [],
        keywords: fs.keywords || [],
      })
        .then((res) => {
          if (res?.data) setLiveStats(res.data);
        })
        .catch(() => {})
        .finally(() => setStatsLoading(false));
    }

    if (contentType === "custom_graph_view") {
      const hasFilters =
        fs.node_types?.length > 0 ||
        fs.edge_types?.length > 0 ||
        fs.keywords?.length > 0 ||
        fs.target_types?.length > 0;
      if (!hasFilters) return;
      setStatsLoading(true);
      getFilteredGraphViewStats({
        filters: {
          node_types: fs.node_types || [],
          edge_types: fs.edge_types || [],
          keywords: fs.keywords || [],
          sources: fs.sources || [],
          target_types: fs.target_types || [],
        },
      })
        .then((res) => {
          if (res.data?.status === 200) setLiveStats(res.data);
        })
        .catch(() => {})
        .finally(() => setStatsLoading(false));
    }
  }, [contentType, preview.filter_summary]);

  const handleAccept = async () => {
    setLoading(true);
    try {
      await acceptShare(meta.invite_id, meta.content_type, meta.document_id);
      setStatus("accepted");
    } catch (e) {
      console.error("Accept share failed:", e);
    }
    setLoading(false);
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await declineShare(meta.invite_id, meta.content_type, meta.document_id);
      setStatus("declined");
    } catch (e) {
      console.error("Decline share failed:", e);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col h-full bg-mainBg">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surfaceBorder/50 flex-shrink-0">
        <div className="flex items-center gap-x-2.5 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: iconColor + "20" }}
          >
            <IconComponent
              className="h-4.5 w-4.5"
              strokeWidth={1.5}
              style={{ color: iconColor }}
            />
          </div>
          <div className="min-w-0">
            <p className="text-[14px] font-light text-mainText truncate">
              {itemName}
            </p>
            <p className="text-[11px] font-light text-mutedText/60">
              {config.label} · Shared by{" "}
              {isOwner ? "you" : meta.owner_name || "Unknown"}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-btnHover transition-colors cursor-pointer flex-shrink-0"
        >
          <X className="h-4 w-4 text-mutedText" strokeWidth={1.5} />
        </button>
      </div>

      {/* ── Content ── */}
      <div
        className="flex-1 overflow-y-auto p-4 space-y-4"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* ── Report: full-height scrollable snippet ── */}
        {contentType === "report" && (
          <div>
            <SectionLabel>Content Preview</SectionLabel>
            <div className="rounded-lg border border-surfaceBorder/50 bg-surface/40 p-4">
              <p className="text-[13px] font-light text-mutedText/80 leading-relaxed whitespace-pre-wrap">
                {preview.snippet || "No preview available"}
              </p>
            </div>
            <div className="flex items-center gap-x-3 text-[11px] font-light text-mutedText/60 mt-3">
              {preview.citation_count > 0 && (
                <span>{preview.citation_count} citations</span>
              )}
              {preview.last_updated && (
                <>
                  {preview.citation_count > 0 && <span>·</span>}
                  <span>
                    Updated{" "}
                    {new Date(preview.last_updated).toLocaleDateString(
                      undefined,
                      {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      },
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Wiki: company stats grid ── */}
        {contentType === "wiki_case" && (
          <div>
            {preview.target && (
              <div className="flex items-center gap-x-2 mb-4">
                <Building2
                  className="h-5 w-5 text-mutedText/60"
                  strokeWidth={1.5}
                />
                <span className="text-[15px] font-light text-mainText">
                  {preview.target}
                </span>
              </div>
            )}
            {preview.stats && (
              <>
                <SectionLabel>Company Stats</SectionLabel>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(preview.stats)
                    .filter(([, v]) => v > 0)
                    .map(([key, count]) => (
                      <StatBox
                        key={key}
                        label={WIKI_STAT_LABELS[key] || key.replace(/_/g, " ")}
                        value={count}
                      />
                    ))}
                </div>
              </>
            )}
            {preview.stats &&
              Object.values(preview.stats).every((v) => v === 0) && (
                <p className="text-[12px] font-light text-mutedText/50">
                  No stats available yet
                </p>
              )}
          </div>
        )}

        {/* ── Feed View: filters + live stats ── */}
        {contentType === "custom_feed_view" && preview.filter_summary && (
          <div className="space-y-4">
            <div>
              <SectionLabel>Filters</SectionLabel>
              <div className="space-y-3">
                {preview.filter_summary.target_types?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-light text-mutedText/40 mb-1.5">
                      Target Types
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.filter_summary.target_types.map((t, i) => (
                        <TagPill key={i} label={t} color="rgb(14,165,233)" />
                      ))}
                    </div>
                  </div>
                )}
                {preview.filter_summary.domains?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-light text-mutedText/40 mb-1.5">
                      Domains
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.filter_summary.domains.map((d, i) => (
                        <TagPill key={i} label={d} color="rgb(168,85,247)" />
                      ))}
                    </div>
                  </div>
                )}
                {preview.filter_summary.keywords?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-light text-mutedText/40 mb-1.5">
                      Keywords
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.filter_summary.keywords.map((k, i) => (
                        <TagPill key={i} label={k} color="rgb(234,179,8)" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="h-px bg-surfaceBorder/40" />

            <div>
              <SectionLabel>Live Stats</SectionLabel>
              {statsLoading ? (
                <div className="flex items-center gap-x-2 text-[12px] text-mutedText/50 py-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading stats…
                </div>
              ) : liveStats ? (
                <div className="grid grid-cols-2 gap-2">
                  {liveStats.total_articles != null && (
                    <StatBox
                      label="Total Articles"
                      value={liveStats.total_articles}
                    />
                  )}
                  {liveStats.total_sources != null && (
                    <StatBox label="Sources" value={liveStats.total_sources} />
                  )}
                  {liveStats.matching_count != null && (
                    <StatBox
                      label="Matching"
                      value={liveStats.matching_count}
                    />
                  )}
                  {liveStats.source_count != null && (
                    <StatBox
                      label="Matching Items"
                      value={liveStats.source_count}
                    />
                  )}
                  {/* Render any other numeric stats */}
                  {Object.entries(liveStats)
                    .filter(
                      ([k, v]) =>
                        typeof v === "number" &&
                        ![
                          "total_articles",
                          "total_sources",
                          "matching_count",
                          "source_count",
                          "status",
                        ].includes(k),
                    )
                    .map(([k, v]) => (
                      <StatBox key={k} label={k.replace(/_/g, " ")} value={v} />
                    ))}
                </div>
              ) : preview.source_count > 0 ? (
                <p className="text-[12px] font-light text-mutedText/70">
                  {preview.source_count.toLocaleString()} matching items
                </p>
              ) : (
                <p className="text-[12px] font-light text-mutedText/50">
                  No stats available
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Graph View: filters + live stats ── */}
        {contentType === "custom_graph_view" && preview.filter_summary && (
          <div className="space-y-4">
            <div>
              <SectionLabel>Graph Filters</SectionLabel>
              <div className="space-y-3">
                {preview.filter_summary.scope && (
                  <div className="flex items-center gap-x-2">
                    <span className="text-[10px] font-light text-mutedText/40">
                      Scope:
                    </span>
                    <TagPill
                      label={preview.filter_summary.scope.replace("_", " ")}
                      color="rgb(34,197,94)"
                    />
                  </div>
                )}
                {preview.filter_summary.node_types?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-light text-mutedText/40 mb-1.5">
                      Node Types
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.filter_summary.node_types.map((t, i) => (
                        <TagPill key={i} label={t} color="rgb(14,165,233)" />
                      ))}
                    </div>
                  </div>
                )}
                {preview.filter_summary.edge_types?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-light text-mutedText/40 mb-1.5">
                      Edge Types
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.filter_summary.edge_types.map((t, i) => (
                        <TagPill key={i} label={t} color="rgb(168,85,247)" />
                      ))}
                    </div>
                  </div>
                )}
                {preview.filter_summary.keywords?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-light text-mutedText/40 mb-1.5">
                      Keywords
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.filter_summary.keywords.map((k, i) => (
                        <TagPill key={i} label={k} color="rgb(234,179,8)" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="h-px bg-surfaceBorder/40" />

            <div>
              <SectionLabel>Live Stats</SectionLabel>
              {statsLoading ? (
                <div className="flex items-center gap-x-2 text-[12px] text-mutedText/50 py-3">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading stats…
                </div>
              ) : liveStats ? (
                <div className="grid grid-cols-2 gap-2">
                  {liveStats.total_nodes != null && (
                    <StatBox label="Nodes" value={liveStats.total_nodes} />
                  )}
                  {liveStats.total_edges != null && (
                    <StatBox label="Edges" value={liveStats.total_edges} />
                  )}
                  {liveStats.total_sources != null && (
                    <StatBox label="Sources" value={liveStats.total_sources} />
                  )}
                  {liveStats.matching_count != null && (
                    <StatBox
                      label="Matching"
                      value={liveStats.matching_count}
                    />
                  )}
                  {/* Render any other numeric stats */}
                  {Object.entries(liveStats)
                    .filter(
                      ([k, v]) =>
                        typeof v === "number" &&
                        ![
                          "total_nodes",
                          "total_edges",
                          "total_sources",
                          "matching_count",
                          "status",
                        ].includes(k),
                    )
                    .map(([k, v]) => (
                      <StatBox key={k} label={k.replace(/_/g, " ")} value={v} />
                    ))}
                </div>
              ) : preview.source_count > 0 ? (
                <p className="text-[12px] font-light text-mutedText/70">
                  {preview.source_count.toLocaleString()} matching items
                </p>
              ) : (
                <p className="text-[12px] font-light text-mutedText/50">
                  No stats available
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Personal message ── */}
        {msg.content && (
          <>
            <div className="h-px bg-surfaceBorder/40" />
            <div>
              <SectionLabel>Message</SectionLabel>
              <p className="text-[13px] font-light text-mutedText italic">
                "{msg.content}"
              </p>
            </div>
          </>
        )}
      </div>

      {/* ── Footer: accept/decline or status ── */}
      <div className="flex-shrink-0 px-4 py-3 border-t border-surfaceBorder/50">
        {isOwner ? (
          <p className="text-[12px] font-light text-mutedText/60 flex items-center justify-center gap-x-1.5">
            <Share2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            You shared this
          </p>
        ) : status === "accepted" ? (
          <p className="text-[12px] font-light text-activeCase flex items-center justify-center gap-x-1.5">
            <Check className="h-3.5 w-3.5" strokeWidth={2} />
            You accepted this share
          </p>
        ) : status === "declined" ? (
          <p className="text-[12px] font-light text-mutedText/60 flex items-center justify-center gap-x-1.5">
            <X className="h-3.5 w-3.5" strokeWidth={2} />
            You declined this share
          </p>
        ) : (
          <div className="flex items-center gap-x-3">
            <button
              onClick={handleAccept}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-x-2 px-4 py-2.5 rounded-lg text-[13px] font-light bg-velBlue/15 text-velBlue hover:bg-velBlue/25 transition-colors cursor-pointer disabled:opacity-50"
            >
              <Check className="h-4 w-4" strokeWidth={2} />
              {meta.target_type === "general" ? "Join" : "Accept"}
            </button>
            <button
              onClick={handleDecline}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-x-2 px-4 py-2.5 rounded-lg text-[13px] font-light bg-surface text-mutedText hover:bg-btnHover transition-colors cursor-pointer disabled:opacity-50"
            >
              <X className="h-4 w-4" strokeWidth={2} />
              Decline
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareInviteDetailPanel;
